/**
 * Top-level App component, wires session provider + routes.
 *
 * Per D-085 (login-first lane subdomains) + D-087 (Lightning shape):
 *
 *   workspace.olytic.com/  → RootRedirect (logged out → /sign-in;
 *                                       logged in → /home)
 *
 *   Auth routes (no shell):
 *     /sign-in, /sign-up, /accept-invite, /forgot-password, /reset-password
 *
 *   User mode (UserShell wraps, TopNav, no sidebar):
 *     /home, /library, /library/:slug, /connectors, /connectors/:slug, /profile
 *
 *   Admin mode (AdminShell wraps, TopNav with "Back to app" + AdminSubNav left):
 *     /admin, /admin/people, /admin/connectors, /admin/assignments,
 *     /admin/activity/{dispatches,spend,audit}, /admin/billing/{overview,buy,
 *     payment-methods,transactions}, /admin/workspace,
 *     /admin/approvals/:short_id (approval-request detail, D-160)
 *
 *   Push 4-1f (2026-07-08): sidenav restructured to 7 slots — Billing
 *   promoted to top-level parent (was Setup > Billing); Transactions moved
 *   from Activity → Billing; Setup parent flattened to Workspace leaf
 *   with tabs. Every prior URL preserved via <Navigate replace /> redirects.
 *
 *   Catch-all (NotFound) lives inside UserShell so unknown URLs render with
 *   the standard chrome instead of breaking layout.
 *
 * Standard users (role='user') can navigate to /admin URLs directly but
 * AdminShell's role gate redirects them back to /home. Admins/owners flip
 * into admin mode via the gear icon in TopNav.
 *
 * The retired Landing.tsx is no longer routed per D-085, the file may exist
 * in src/pages/ for marketing-copy reference but no route renders it.
 */

import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { AdminShell } from './components/AdminShell.tsx';
import { BuyCreditsProvider } from './components/BuyCreditsProvider.tsx';
import { ErrorBoundary } from './components/ErrorBoundary.tsx';
import { RouteProgressProvider } from './components/RouteProgress.tsx';
import { SessionExpiryListener } from './components/SessionExpiryListener.tsx';
import { SkeletonDetail } from './components/Skeleton.tsx';
import { ToastProvider, ToastRegion } from './components/Toast.tsx';
import { UserShell } from './components/UserShell.tsx';
import { BrandThemeProvider } from './hooks/useBrandTheme.tsx';
import { TitleManager } from './hooks/useDocumentTitle.tsx';
import { ScrollToTop } from './hooks/useScrollToTop.tsx';
import { SessionProvider, useSession } from './hooks/useSession.tsx';
import { ThemeProvider } from './hooks/useTheme.tsx';
import { AcceptInvite } from './pages/AcceptInvite.tsx';
import { Connectors } from './pages/Connectors.tsx';
import { Forbidden } from './pages/Forbidden.tsx';
import { ForgotPassword } from './pages/ForgotPassword.tsx';
import { Home } from './pages/Home.tsx';
import { Knowledge } from './pages/Knowledge.tsx';
import { LibraryDetail } from './pages/LibraryDetail.tsx';
// Push 4-1b (2026-07-01): pages/Library.tsx → pages/Market.tsx (URL swap
// /library legacy-redirect → /market as canonical; Market is the discovery
// surface for Group 1 sourced capabilities per LOCKED MENTAL MODEL).
// LibraryDetail.tsx keeps its file name — it's the market-template detail
// page, mounted at /market/:short_id.
import { Market } from './pages/Market.tsx';
import { Models } from './pages/Models.tsx';
import { NotFound } from './pages/NotFound.tsx';
import { OfflineError } from './pages/OfflineError.tsx';
// Reused pages that live in the admin shell now (their internal content is
// admin-shaped, list of org-wide rows; in user mode they'd be useless).
// Kept at the original path for now to avoid a file move; routes mount them
// under /admin/*.
import { People } from './pages/People.tsx';
import { ResetPassword } from './pages/ResetPassword.tsx';
import { ServerError } from './pages/ServerError.tsx';
import { SessionExpired } from './pages/SessionExpired.tsx';
// --- Eager-loaded routes ---
// Round 7 Item 41 — these are eager-loaded by intent. Splitting them off
// behind lazy() would produce worse UX than the bytes saved:
//   - Auth pages: pre-auth landings; lazy = janky first impression.
//   - Home / Library: primary user-mode entrypoints, hit every session.
//   - BrandedFaultRoute pages (NotFound / Forbidden / etc.): render on
//     error — fetching a chunk during a fault state is the worst time.
import { SignIn } from './pages/SignIn.tsx';
import { SignUp } from './pages/SignUp.tsx';
import { ToolDetail } from './pages/ToolDetail.tsx';
import { VerifyEmail } from './pages/VerifyEmail.tsx';

// Push 5-post-smoke-33 (Sub-agent V, 2026-07-15): Activity wrapper import
// dropped — AnalyticsActivity.tsx now owns the raw event-feed surface
// directly (with its own filter bar + tabs). Legacy Activity.tsx page is
// currently unreferenced; kept in-tree until a follow-up sweep prunes it.
// import { Activity } from './pages/Activity.tsx';

// --- Lazy-loaded routes (Round 7 Item 41) ---
// Every page in this block is named-export, not default-export. The
// wrapper-pattern (`.then(m => ({ default: m.Foo }))`) is preferred over
// adding `export default` to each page file: zero per-file changes, the
// named-export shape stays canonical, and the wrapper line is the single
// place readers can see the lazy boundary.
//
// Heavy detail pages + the admin tree + chat tree + onboarding wizard +
// rarely-visited legal pages get split. Hitting any of these for the first
// time per session pulls a separate chunk; subsequent visits cache-hit.
const Chat = lazy(() => import('./pages/Chat.tsx').then((m) => ({ default: m.Chat })));
const ConnectorDetail = lazy(() =>
  import('./pages/ConnectorDetail.tsx').then((m) => ({ default: m.ConnectorDetail })),
);
const KnowledgeDetail = lazy(() =>
  import('./pages/KnowledgeDetail.tsx').then((m) => ({ default: m.KnowledgeDetail })),
);
const ModelDetail = lazy(() =>
  import('./pages/ModelDetail.tsx').then((m) => ({ default: m.ModelDetail })),
);
const ActivityDetail = lazy(() =>
  import('./pages/ActivityDetail.tsx').then((m) => ({ default: m.ActivityDetail })),
);
// Push 4-1d-s Item 2 (2026-07-06) — MyActivity is the user-scoped list at
// /activity ("your dispatches"). Standard app = user scoped, admin = team
// scoped per Ean's mental model. AdminActivityDetail is the admin-mode
// wrapper for /admin/activity/:short_id (thin mode='admin' pass-through).
const MyActivity = lazy(() =>
  import('./pages/MyActivity.tsx').then((m) => ({ default: m.MyActivity })),
);
const AdminActivityDetail = lazy(() =>
  import('./pages/admin/AdminActivityDetail.tsx').then((m) => ({
    default: m.AdminActivityDetail,
  })),
);
const Welcome = lazy(() => import('./pages/Welcome.tsx').then((m) => ({ default: m.Welcome })));
// Push 4-1e (2026-07-07) — /settings + /legal unified tabbed shells.
// Legacy /legal/{slug} paths redirect to /legal?tab= unified surface.
// Standard-app user Settings lives at pages/settings/index.tsx (per Ean
// R5 adjudication — new file lands with the descriptive path so the
// admin Settings.tsx keeps its established filename).
const UserSettings = lazy(() =>
  import('./pages/settings/index.tsx').then((m) => ({ default: m.Settings })),
);
const Legal = lazy(() => import('./pages/Legal.tsx').then((m) => ({ default: m.Legal })));
// PLAT-101: Olytic operators only, outside /admin (which is the caller's own
// workspace). The API's 403 decides access; no nav links here.
const OperatorCredits = lazy(() =>
  import('./pages/operator/OperatorCredits.tsx').then((m) => ({ default: m.OperatorCredits })),
);

// Admin-mode pages — entire tree lazy. Standard users never load this code.
const AdminHome = lazy(() =>
  import('./pages/admin/AdminHome.tsx').then((m) => ({ default: m.AdminHome })),
);
const AdminConnectors = lazy(() =>
  import('./pages/admin/AdminConnectors.tsx').then((m) => ({ default: m.AdminConnectors })),
);
const AdminConnectorDetail = lazy(() =>
  import('./pages/admin/AdminConnectorDetail.tsx').then((m) => ({
    default: m.AdminConnectorDetail,
  })),
);
// Push 84b (D-199) — BYO Pattern C guided install wizard. Reached from the
// "+ Add BYO Connector" CTA on /admin/connectors + the "Have your own MCP?
// Bring it →" discovery link on /market + /admin/market.
const AdminBYOConnectorInstall = lazy(() =>
  import('./pages/admin/AdminBYOConnectorInstall.tsx').then((m) => ({
    default: m.AdminBYOConnectorInstall,
  })),
);
const AdminMarketDetail = lazy(() =>
  import('./pages/admin/AdminMarketDetail.tsx').then((m) => ({ default: m.AdminMarketDetail })),
);
// Push 4-1d-m (2026-07-06): kind-split market detail — models get a distinct
// route + component pair on both user and admin surfaces. Connector variants
// keep their existing components (LibraryDetail / AdminMarketDetail) but move
// to /market/connectors/:short_id + /admin/market/connectors/:short_id.
const ModelMarketDetail = lazy(() =>
  import('./pages/ModelMarketDetail.tsx').then((m) => ({ default: m.ModelMarketDetail })),
);
const AdminModelMarketDetail = lazy(() =>
  import('./pages/admin/AdminModelMarketDetail.tsx').then((m) => ({
    default: m.AdminModelMarketDetail,
  })),
);
const AdminAssignments = lazy(() =>
  import('./pages/admin/AdminAssignments.tsx').then((m) => ({ default: m.AdminAssignments })),
);
const PermissionSets = lazy(() =>
  import('./pages/admin/PermissionSets.tsx').then((m) => ({ default: m.PermissionSets })),
);
const PermissionSetDetail = lazy(() =>
  import('./pages/admin/PermissionSetDetail.tsx').then((m) => ({
    default: m.PermissionSetDetail,
  })),
);
const AdminPromptDetail = lazy(() =>
  import('./pages/admin/AdminPromptDetail.tsx').then((m) => ({ default: m.AdminPromptDetail })),
);
const AdminModelDetail = lazy(() =>
  import('./pages/admin/AdminModelDetail.tsx').then((m) => ({ default: m.AdminModelDetail })),
);
const PersonDetail = lazy(() =>
  import('./pages/admin/PersonDetail.tsx').then((m) => ({ default: m.PersonDetail })),
);
const AdminBilling = lazy(() =>
  import('./pages/admin/AdminBilling.tsx').then((m) => ({ default: m.AdminBilling })),
);
const AdminBillingAdvanced = lazy(() =>
  import('./pages/admin/AdminBillingAdvanced.tsx').then((m) => ({
    default: m.AdminBillingAdvanced,
  })),
);
// Sub-agent TTTTT (2026-07-31) — Analytics 3-tab rebuild. Prior
// AdminInsights + AnalyticsInsights + AnalyticsSpend lazy imports
// retired; their bodies are absorbed into AnalyticsOverview (callouts +
// top-3 splits) and AnalyticsReports (drilldown pivots). See
// /admin/analytics/{overview,reports,activity} routes below for the
// 3-surface IA (was 4).
const AnalyticsOverview = lazy(() =>
  import('./pages/admin/analytics/AnalyticsOverview.tsx').then((m) => ({
    default: m.AnalyticsOverview,
  })),
);
const AnalyticsReports = lazy(() =>
  import('./pages/admin/analytics/AnalyticsReports.tsx').then((m) => ({
    default: m.AnalyticsReports,
  })),
);
const AnalyticsActivity = lazy(() =>
  import('./pages/admin/analytics/AnalyticsActivity.tsx').then((m) => ({
    default: m.AnalyticsActivity,
  })),
);
// Push 4-1e-cleanup-fu2 (2026-07-08): /admin/inbox legacy-redirects to
// /admin/people?tab=approvals (tab renamed from awaiting-approval).
const AdminKnowledge = lazy(() =>
  import('./pages/admin/AdminKnowledge.tsx').then((m) => ({ default: m.AdminKnowledge })),
);
const AdminModels = lazy(() =>
  import('./pages/admin/AdminModels.tsx').then((m) => ({ default: m.AdminModels })),
);
const AdminCredentials = lazy(() =>
  import('./pages/admin/AdminCredentials.tsx').then((m) => ({ default: m.AdminCredentials })),
);

// Phase 2 Block 2B Tier 5 (2026-06-30) — operator-only record stub pages.
// 9 thin route components share OperatorRecordStub; admin lands on the
// "platform owns this" shell. Per execution plan § Block 2B Tier 5:
// Universal-or-Nothing — every URL in the 48-table inventory renders
// something intentional, including the operator-managed ones. All 9
// stubs share a single source module + a single bundle chunk via the
// lazy() loader (admin-mode-only, never pulled into the user bundle).
const PricingConfigStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.PricingConfigStub })),
);
const AccountRoutingStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.AccountRoutingStub })),
);
const UserRoutingStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.UserRoutingStub })),
);
const OAuthClientStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.OAuthClientStub })),
);
const OAuthAuthorizationStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({
    default: m.OAuthAuthorizationStub,
  })),
);
const OperatorAlertStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.OperatorAlertStub })),
);
const IncidentStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.IncidentStub })),
);
const StripeWebhookStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.StripeWebhookStub })),
);
const SchemaMigrationStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.SchemaMigrationStub })),
);
// Push 4-0 (2026-07-01) — Category G stub for llm__key_shared.
const LlmKeySharedStub = lazy(() =>
  import('./pages/admin/OperatorRecordPages.tsx').then((m) => ({ default: m.LlmKeySharedStub })),
);

// Phase 2 Block 2B Tier 2 (2026-06-30) — first 2 of 6 NEW workflow/billing
// detail pages. Each is its own component since the data shape + per-table
// spice differ (unlike Tier 5's shared OperatorRecordStub). Remaining 4
// (Token Debit, Approval Request, Invitation, Event Log) carry to
// subsequent sessions per the multi-session arc protocol.
const NotificationDetail = lazy(() =>
  import('./pages/NotificationDetail.tsx').then((m) => ({ default: m.NotificationDetail })),
);
const TokenGrantDetail = lazy(() =>
  import('./pages/admin/TokenGrantDetail.tsx').then((m) => ({ default: m.TokenGrantDetail })),
);
const TokenDebitDetail = lazy(() =>
  import('./pages/admin/TokenDebitDetail.tsx').then((m) => ({ default: m.TokenDebitDetail })),
);
const InvitationDetail = lazy(() =>
  import('./pages/admin/InvitationDetail.tsx').then((m) => ({ default: m.InvitationDetail })),
);

// Phase 2 Block 2B Tier 4 (2026-06-30) — 9 junction audit pages (Category H).
// All 9 share JunctionRecordStub primitive + dispatcher endpoint. Single
// source module + one lazy() chunk per route (chunks share source; the
// bundler dedupes the import). Same pattern as Tier 5's OperatorRecordPages.
const PermissionSetUserJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.PermissionSetUserJct,
  })),
);
const PermissionSetConnectorJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.PermissionSetConnectorJct,
  })),
);
const PermissionSetPromptJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.PermissionSetPromptJct,
  })),
);
const PermissionSetModelJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.PermissionSetModelJct,
  })),
);
const ConnectorAssignmentJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.ConnectorAssignmentJct,
  })),
);
const UserConnectorAuthJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.UserConnectorAuthJct,
  })),
);
const ModelAssignmentJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({ default: m.ModelAssignmentJct })),
);
const PromptAssignmentJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({ default: m.PromptAssignmentJct })),
);
const ChatSessionAssignmentJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.ChatSessionAssignmentJct,
  })),
);

// Phase 2 Block 2B Tier 3 / 6 / 7 / 48th (2026-06-30) — 12 detail pages
// (10 unique + 2 dual-mode user variants) via shared SubRecordDetail
// primitive + dispatcher endpoints. Completes Universal-or-Nothing
// coverage across all 48 Olytic-owned tables.
const ResourceInstanceAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ResourceInstanceAdmin })),
);
const ResourceInstanceUser = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ResourceInstanceUser })),
);
const McpPromptInstanceAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.McpPromptInstanceAdmin })),
);
const McpPromptInstanceUser = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.McpPromptInstanceUser })),
);
const ChatFolder = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ChatFolder })),
);
const ChatMessage = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ChatMessage })),
);
const ToolTemplateAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ToolTemplateAdmin })),
);
const ResourceTemplateAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ResourceTemplateAdmin })),
);
const McpPromptTemplateAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.McpPromptTemplateAdmin })),
);
const ProviderTemplateAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ProviderTemplateAdmin })),
);
const ProductAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ProductAdmin })),
);
const NotificationRecipientAdmin = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({
    default: m.NotificationRecipientAdmin,
  })),
);

// Push 5-post-smoke-13 Sub-agent D (2026-07-14) — promoted the
// prior modal-only notification recipient management surface to a
// full detail page. There's ONE implicit recipient group per workspace
// (Migration 0130 introduced `sys__notification_recipient` — the
// junction rows — with no separate group table today). The URL is a
// fixed slot so the "Manage recipients" affordance in Settings can
// deep-link here as the "open full view" destination; a future
// multi-group evolution slots into
// `/admin/notifications/recipient-groups/:short_id` without a
// component rewrite.
const NotificationRecipientGroupDetail = lazy(() =>
  import('./pages/admin/NotificationRecipientGroupDetail.tsx').then((m) => ({
    default: m.NotificationRecipientGroupDetail,
  })),
);

// Phase 2 closure + Phase 4 pre-plumbing (2026-06-30):
// - GAP #4a/b: Key + Provider Category B admin pages
// - GAP #4c: knowledge__prompt_template Category A dual — RETIRED in Push
//   4-1c-b Migration 0144 (kpt dropped; prompts now Group 2 BYO with no
//   Category A dual — user + admin surfaces render workspace_selfservice
//   prompts directly, no template layer).
// - Phase 4: Document (user + admin) + DocumentChunk + Folder junctions
const KeyDetail = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.KeyDetail })),
);
const ProviderDetail = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.ProviderDetail })),
);
// Push 4-1c-c (2026-07-01): KnowledgePromptTemplate lazy imports removed.
// kpt table dropped in Migration 0144. Routes deleted below.
// Push 5-C-cont (2026-07-11): DocumentDetail upgrades from the thin
// SubRecordLoader wrapper to a full RecordDetailLayout with tabs
// (Details / Chunks / Activity), InlineEditTitle rename, ConfirmDialog
// delete, and folder-link chip. User + admin wrappers share
// pages/document-detail/DocumentDetailShared.tsx via mode='user'|'admin'.
// Read endpoint /api/me/library/documents/:short_id (RLS Tier 3 SELECT);
// admin writes route to /api/admin/library/documents/:short_id/{rename,move,delete}.
const DocumentDetailUser = lazy(() =>
  import('./pages/DocumentDetail.tsx').then((m) => ({ default: m.DocumentDetail })),
);
const DocumentDetailAdmin = lazy(() =>
  import('./pages/admin/AdminDocumentDetail.tsx').then((m) => ({ default: m.AdminDocumentDetail })),
);
const DocumentChunkDetail = lazy(() =>
  import('./pages/admin/SubRecordPages.tsx').then((m) => ({ default: m.DocumentChunkDetail })),
);
// Push 4-1b (2026-07-01): FolderDetail user + admin — Category A dual
// per folder-as-share-unit pattern. Mount at /library/folders/:short_id
// and /admin/library/folders/:short_id. Empty-state graceful (no rows
// pre-Phase-4E ingestion).
const FolderDetailUser = lazy(() =>
  import('./pages/FolderDetail.tsx').then((m) => ({ default: m.FolderDetailUser })),
);
const FolderDetailAdmin = lazy(() =>
  import('./pages/admin/AdminFolderDetail.tsx').then((m) => ({ default: m.AdminFolderDetail })),
);
// Push 4-1b (2026-07-01): DocumentList → LibraryList (file + class rename)
// per LOCKED MENTAL MODEL — "Library" is the collection surface, mounts at
// /library (formerly /documents). AdminDocumentList → AdminLibraryList same
// pattern, mounts at /admin/library. DocumentDetailUser/Admin export names
// stay in SubRecordPages.tsx — "document" is the record noun, unchanged.
const LibraryList = lazy(() =>
  import('./pages/LibraryList.tsx').then((m) => ({ default: m.LibraryList })),
);
const AdminLibraryList = lazy(() =>
  import('./pages/admin/AdminLibraryList.tsx').then((m) => ({ default: m.AdminLibraryList })),
);
// Push 4-1 (2026-07-01) — renamed from Document* to Folder* — folder-level
// junctions replace pre-plumbing document-level junctions. Same shape.
const FolderAssignmentJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({ default: m.FolderAssignmentJct })),
);
const PermissionSetFolderJct = lazy(() =>
  import('./pages/admin/JunctionRecordPages.tsx').then((m) => ({
    default: m.PermissionSetFolderJct,
  })),
);
// Lazy admin-shell-rendered pages (component lives at /pages root for legacy
// reasons but the route is /admin/*). Splitting these moves admin-only code
// out of the user bundle.
const Settings = lazy(() => import('./pages/Settings.tsx').then((m) => ({ default: m.Settings })));

// Phase 2 Block 2A close sweep (2026-06-30): the four Legacy*Redirect helpers
// (LegacyLibraryRedirect, LegacyCatalogRedirect, LegacyAdminCatalogRedirect,
// KnowledgeRedirect) were deleted here. Cluster A removed their backing Route
// entries; the helpers had become orphaned dead code referencing the
// old :slug param that no longer exists in the route tree. Legacy bookmarks
// to /library/:slug or /knowledge/:slug now fall through to the SPA's
// NotFound boundary, which is the desired outcome — the URL discipline
// shifted to short_id and the legacy slug values can no longer resolve to
// a meaningful record even at the redirect layer.

function RootRedirect() {
  const { status } = useSession();
  if (status === 'loading') {
    // Render nothing on initial load, /api/me typically resolves in <100ms
    // so a flash of a loading spinner is more disruptive than blank.
    return null;
  }
  if (status === 'authenticated') {
    return <Navigate to="/home" replace />;
  }
  return <Navigate to="/sign-in" replace />;
}

/**
 * Push 4-1f (2026-07-08): /admin/billing?tab= legacy redirects to their
 * new IA homes. Push 4-1f refines Push 4-1e's chain to match the new
 * canonical 4-tab shape (Overview / Buy / Payment methods / Transactions):
 *   history → /admin/billing/transactions (renamed, stays on billing page)
 *   usage-by-user → /admin/activity/spend
 *   usage-by-capability → /admin/activity/spend
 *   people → /admin/people
 * Any other ?tab= (overview/buy/payment-methods/transactions) or no ?tab=
 * keeps the /admin/billing shell — AdminBilling handles path-based routing
 * for the 4 canonical children.
 */
function AdminBillingWithLegacyTabRedirects() {
  // Push 5-post-smoke-20 (2026-07-14) — the 4-inner-tab Billing shape retired;
  // legacy ?tab= params all map to the new consolidated Billing / Usage pages.
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const t = params.get('tab');
  if (t === 'history') {
    return <Navigate to="/admin/workspace/billing" replace />;
  }
  if (t === 'usage-by-user' || t === 'usage-by-capability') {
    return <Navigate to="/admin/analytics/reports?pivot=user" replace />;
  }
  if (t === 'people') return <Navigate to="/admin/people" replace />;
  if (t === 'buy') return <Navigate to="/admin/workspace/usage" replace />;
  return <AdminBilling />;
}

/**
 * Push 4-1d-m (2026-07-06): market route split by kind. All market template
 * detail routes now live under `/market/{kind}/:short_id` (user) and
 * `/admin/market/{kind}/:short_id` (admin) so URL prefix communicates the
 * category. Kinds: 'connectors' (cnt-prefix short_ids) + 'models' (mdt-prefix).
 * Legacy /market/:short_id bookmarks resolve here via short_id prefix
 * inspection — cnt→connectors, mdt→models, unknown falls through to /not-found.
 *
 * Same shape as the junction route harmonization arc (LegacyJunctionRedirect
 * factory below): one factory per surface (user vs admin) so the resolution
 * stays grep-able by URL segment.
 */
function LegacyMarketRedirect() {
  const { short_id = '' } = useParams<{ short_id: string }>();
  if (short_id.startsWith('cnt')) {
    return <Navigate to={`/market/connectors/${short_id}`} replace />;
  }
  if (short_id.startsWith('mdt')) {
    return <Navigate to={`/market/models/${short_id}`} replace />;
  }
  // Unknown prefix — fall through to the NotFound boundary. The catch-all
  // inside UserShell handles unmatched routes; hitting an invalid /market/:x
  // bookmark should surface a branded 404 rather than a silent redirect to
  // the market list.
  return <Navigate to="/not-found" replace />;
}
function LegacyAdminMarketRedirect() {
  const { short_id = '' } = useParams<{ short_id: string }>();
  if (short_id.startsWith('cnt')) {
    return <Navigate to={`/admin/market/connectors/${short_id}`} replace />;
  }
  if (short_id.startsWith('mdt')) {
    return <Navigate to={`/admin/market/models/${short_id}`} replace />;
  }
  return <Navigate to="/not-found" replace />;
}

/**
 * Push 4-1d-k-really-final (2026-07-02): junction route harmonization.
 * All junction record detail routes now live under `/admin/junction/{kind}/:short_id`
 * so Category H (Tier 4) pages are discoverable by URL prefix alone. Seven
 * legacy routes (permission-set-user/-connector/-model/-prompt,
 * connector/model/prompt-assignment) get a redirect stub at the old path
 * that forwards to the harmonized new path. `<Navigate replace />` avoids
 * a stub entry in browser history.
 *
 * Factory shape (vs. seven inline components): one function per kind kept
 * inline for grep-ability — each redirect surfaces its `kind` in the render
 * path so future readers can trace which junction table is affected.
 */
function makeLegacyJunctionRedirect(kind: string) {
  return function LegacyJunctionRedirect() {
    const { short_id = '' } = useParams<{ short_id: string }>();
    return <Navigate to={`/admin/junction/${kind}/${short_id}`} replace />;
  };
}
const LegacyPermissionSetUserRedirect = makeLegacyJunctionRedirect('permission-set-user');
const LegacyPermissionSetConnectorRedirect = makeLegacyJunctionRedirect('permission-set-connector');
const LegacyPermissionSetModelRedirect = makeLegacyJunctionRedirect('permission-set-model');
const LegacyPermissionSetPromptRedirect = makeLegacyJunctionRedirect('permission-set-prompt');
const LegacyConnectorAssignmentRedirect = makeLegacyJunctionRedirect('connector-assignment');
const LegacyModelAssignmentRedirect = makeLegacyJunctionRedirect('model-assignment');
const LegacyPromptAssignmentRedirect = makeLegacyJunctionRedirect('prompt-assignment');
// Push 4-1d-k-truly-final (2026-07-02) — the last two junction routes
// harmonized. All 9 junction detail routes now under /admin/junction/{kind}/:short_id.
const LegacyUserConnectorAuthorizationRedirect = makeLegacyJunctionRedirect(
  'user-connector-authorization',
);
const LegacyChatSessionAssignmentRedirect = makeLegacyJunctionRedirect('chat-session-assignment');

export function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <SessionProvider>
          <BrandThemeProvider>
            <ThemeProvider>
              <RouteProgressProvider>
                <BuyCreditsProvider>
                  <TitleManager />
                  <ScrollToTop />
                  <ToastRegion />
                  <SessionExpiryListener />
                  {/* Round 7 Item 41 — single Suspense wraps the whole router. Vite
           handles route-level lazy chunks cleanly with one boundary; per-route
           Suspense would be ~30 extra wrappers for the same effect. Fallback
           is SkeletonDetail (branded loading shape) — never a blank flash. */}
                  <Suspense fallback={<SkeletonDetail />}>
                    <Routes>
                      {/* Round 5 Item 7 — branded fault routes (no shell wrapper; they
           own their own minimal chrome so a 403/404/500 looks intentional
           rather than "did something break the layout"). */}
                      <Route path="/forbidden" element={<Forbidden />} />
                      <Route path="/session-expired" element={<SessionExpired />} />
                      <Route path="/server-error" element={<ServerError />} />
                      <Route path="/offline" element={<OfflineError />} />

                      {/* Round 6.5 Item 29 — legal pages reachable pre-auth so footer
           links work from sign-up. Push 4-1e (2026-07-07): unified /legal
           tabbed shell. Push 4-1g (2026-07-08): route stays outside
           UserShell but Legal.tsx now auto-adapts to auth state —
           signed-in users get full TopNav + .app-shell.user-mode chrome
           inside the component (nav bar preserved end-to-end, mirroring
           /settings + /profile so nav-out feels the same); signed-out
           users get a minimal branded chrome (logo + Sign in / Sign up)
           with FooterChrome. Both branches share the same page body,
           only the outer chrome forks on session status. Legacy
           /legal/{slug} redirects into the shell via ?tab= param. */}
                      <Route path="/legal" element={<Legal />} />
                      <Route
                        path="/legal/privacy"
                        element={<Navigate to="/legal?tab=privacy" replace />}
                      />
                      <Route
                        path="/legal/terms"
                        element={<Navigate to="/legal?tab=terms" replace />}
                      />
                      <Route
                        path="/legal/acceptable-use"
                        element={<Navigate to="/legal?tab=acceptable-use" replace />}
                      />
                      <Route
                        path="/legal/cookies"
                        element={<Navigate to="/legal?tab=cookies" replace />}
                      />

                      {/* Root + auth (no shell) */}
                      <Route path="/" element={<RootRedirect />} />
                      <Route path="/sign-in" element={<SignIn />} />
                      <Route path="/sign-up" element={<SignUp />} />
                      <Route path="/accept-invite" element={<AcceptInvite />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/verify-email" element={<VerifyEmail />} />
                      {/* D-168 owner onboarding wizard — standalone, no shell. */}
                      <Route path="/welcome" element={<Welcome />} />

                      {/* User mode (UserShell, TopNav, no sidebar) */}
                      <Route element={<UserShell />}>
                        <Route path="/home" element={<Home />} />
                        <Route path="/market" element={<Market />} />
                        {/* Phase 2 Block 2A (2026-06-30): record detail URLs are now
             `<route>/<short_id>` per the URL identifier discipline lock.
             short_id is a 15-char identifier (3-char table prefix + 12-char
             base62) generated via olytic.generate_short_id() and populated
             on every Olytic row (Phase 1). Closes the multi-instance shared-
             slug collision bug structurally — each row has a unique short_id
             with a per-table prefix.

             Push 4-1d-m (2026-07-06): market detail routes now kind-prefixed
             (/market/connectors/:short_id + /market/models/:short_id) so URL
             communicates category. Category A cross-schema discipline:
             market routes source workspace_directory TEMPLATE tables (shared
             catalog), never workspace_selfservice instances. Legacy
             /market/:short_id resolves via LegacyMarketRedirect (dispatches
             on short_id prefix — cnt→connectors, mdt→models). */}
                        <Route path="/market/connectors/:short_id" element={<LibraryDetail />} />
                        <Route path="/market/models/:short_id" element={<ModelMarketDetail />} />
                        <Route path="/market/:short_id" element={<LegacyMarketRedirect />} />
                        {/* List-page legacy redirects preserved (/library → /market,
             /catalog → /market). Slug-shaped legacy DETAIL URLs
             (/library/:slug, /catalog/:slug) are intentionally NOT
             preserved — they 404 by design per the short_id discrimination
             lock. */}
                        <Route path="/catalog" element={<Navigate to="/market" replace />} />
                        {/* Push 4-1b (2026-07-01): /library is now the Library docs
             list surface (was legacy redirect → /market). Legacy shape
             /catalog → /market preserved. */}
                        <Route path="/connectors" element={<Connectors />} />
                        <Route path="/connectors/:short_id" element={<ConnectorDetail />} />
                        {/* Phase 2 Block 2A: tool URL flattened from
             /connectors/:slug/tools/:tool_id → /tools/:short_id.
             Sub-records get flat URLs because each table's short_id has a
             unique prefix (cni for connector instances, tli for tool
             instances, etc.) — no risk of collision. */}
                        <Route path="/tools/:short_id" element={<ToolDetail />} />
                        <Route path="/chat" element={<Chat />} />
                        {/* Push #23 Track C: URL-routed chat sessions. /chat/:sessionId
              restores a specific conversation; bare /chat lazily creates a
              new session on the first send (existing behavior). */}
                        {/* Phase 2 Block 2A Cluster C (2026-06-30) — :sessionId → :short_id.
             :short_id is chat__session.short_id (chs-prefix). Dual-accept on
             all /api/chat/sessions/[id]/* endpoints means existing UUID URLs
             continue to work; new URLs share the short_id discipline used
             everywhere else in the app. Internal sessionId state continues
             to hold whatever the URL provides (UUID or short_id); the dual-
             accept server resolves to UUID before row operations. */}
                        <Route path="/chat/:short_id" element={<Chat />} />
                        {/* Per Ean 2026-06-09 naming alignment: pillar URL is /prompts to
             match the cross-site display name. Component files stay as
             Knowledge.tsx (schema namespace = knowledge__*), but everything
             the user sees says "Prompts". */}
                        <Route path="/prompts" element={<Knowledge />} />
                        {/* Phase 2 Block 2A Cluster B (2026-06-30) — :slug → :short_id.
             :short_id is knowledge__prompt.short_id (kpi-prefix). */}
                        <Route path="/prompts/:short_id" element={<KnowledgeDetail />} />
                        {/* Legacy redirect: bookmarks to /knowledge land on the new path. */}
                        <Route path="/knowledge" element={<Navigate to="/prompts" replace />} />
                        <Route path="/models" element={<Models />} />
                        {/* Phase 2 Block 2A Cluster B — :slug → :short_id.
             :short_id is llm__model_instance.short_id (lmi-prefix). */}
                        <Route path="/models/:short_id" element={<ModelDetail />} />
                        {/* Activity record — foundation-level drill-down per Ean 2026-06-09.
             Lives under UserShell so standard users CAN drill into their own
             dispatches from /home recent activity. RLS Pattern 2 on
             sys__event_log gates visibility (users see own rows; admins see
             all account rows).
             Push 4-1d-s Item 2 (2026-07-06): added user-scoped LIST route
             at /activity that hits /api/me/activity. Standard app is user
             scoped per Ean's mental model verbatim ("standard app is user
             scoped, admin is team scoped"). Team-wide list stays at
             /admin/activity under the AdminShell tree below. */}
                        <Route path="/activity" element={<MyActivity />} />
                        <Route path="/activity/:short_id" element={<ActivityDetail />} />
                        {/* Phase 2 Block 2B Tier 2 (2026-06-30) — notification record
              detail. User-side permalink for any sys__notification row;
              the bell dropdown rows can deep-link here for the full
              context (kind + body + linked-record jump). Mark-read fires
              on first view; explicit "Mark read" button is the relief
              valve. RLS Tier 3 (recipient or admin/owner). */}
                        <Route path="/notifications/:short_id" element={<NotificationDetail />} />
                        {/* Legacy /requests/:short_id URLs (approval workflow retired
              per D-198 Migration 0158) redirect to Home. */}
                        <Route
                          path="/requests/:short_id"
                          element={<Navigate to="/home" replace />}
                        />
                        {/* Phase 2 Block 2B Tier 3 (2026-06-30) — user-side sub-record
              detail pages. Resource Instance + MCP-prompt Instance are
              Category A dual (same URL shape as admin but user-shell
              chrome + back-link to /connectors). Chat Folder + Chat Message
              are Category C (standard-only per privacy). */}
                        <Route path="/resources/:short_id" element={<ResourceInstanceUser />} />
                        <Route path="/mcp-prompts/:short_id" element={<McpPromptInstanceUser />} />
                        <Route path="/chat/folders/:short_id" element={<ChatFolder />} />
                        <Route path="/messages/:short_id" element={<ChatMessage />} />
                        {/* Phase 2 closure GAP #4c + Phase 4 pre-plumbing (2026-06-30) */}
                        {/* Push 4-1c-c (2026-07-01): /market/knowledge route removed —
             knowledge__prompt_template dropped in Migration 0144. Prompts
             are Group 2 BYO, no Market surface. */}
                        {/* Push 4-1b: /documents → /library. Documents are RECORDS
             inside the Library collection surface. Folder detail routes
             live under /library/folders/:short_id. */}
                        <Route path="/library" element={<LibraryList />} />
                        <Route path="/library/:short_id" element={<DocumentDetailUser />} />
                        <Route path="/library/folders/:short_id" element={<FolderDetailUser />} />
                        <Route path="/library/chunks/:short_id" element={<DocumentChunkDetail />} />
                        {/* Push 5-C (2026-07-11) — /knowledge/documents/:short_id is the
             URL emitted by <CitationChip /> inside chat responses. Alias
             to the existing Document detail surface so citation links
             resolve immediately. The full tabbed Document detail page
             (Details / Chunks / Activity + rename/move/delete) rolls in
             at Push 5-C-cont. */}
                        <Route
                          path="/knowledge/documents/:short_id"
                          element={<DocumentDetailUser />}
                        />
                        {/* Push 4-1e (2026-07-07): /profile legacy redirects into the
             unified /settings surface as the Profile tab. */}
                        <Route
                          path="/profile"
                          element={<Navigate to="/settings?tab=profile" replace />}
                        />
                        <Route path="/settings" element={<UserSettings />} />
                        <Route path="/operator/credits" element={<OperatorCredits />} />
                        <Route path="*" element={<NotFound />} />
                      </Route>

                      {/* Admin mode (AdminShell, TopNav + AdminSubNav) */}
                      <Route element={<AdminShell />}>
                        <Route path="/admin" element={<AdminHome />} />
                        {/* Round 10 Item 75 — admin parent-route redirects. The AdminSubNav
              tree has collapsible parents whose labels are NOT clickable URLs
              in the sidebar, but bookmarks / typo'd URLs / external links may
              still hit /admin/permissions etc. Without these redirects, those
              URLs render 404. Forward each parent to its canonical primary
              leaf. For Capabilities, /admin/models wins per D-167 M-C-P pillar
              order (Models first).
              Push 4-1f (2026-07-08): Setup parent retired (flattened to
              Workspace leaf); /admin/setup + variants legacy-redirect to
              /admin/workspace. Billing promoted to top-level parent —
              /admin/billing legacy-redirect to /admin/billing/overview. */}
                        <Route
                          path="/admin/permissions"
                          element={<Navigate to="/admin/permission-sets" replace />}
                        />
                        <Route
                          path="/admin/capabilities"
                          element={<Navigate to="/admin/models" replace />}
                        />
                        {/* D-159: admin Market mirrors user-mode /market. The LIST page
             (Library) is still shared — it's a catalog browser that role-
             branches on canInstall — but the DETAIL page is forked into
             AdminMarketDetail (Push #43) so admin install/configure/assign
             affordances don't leak into the user-mode read-mostly variant.
             Old /admin/catalog paths forward for any saved admin bookmarks. */}
                        <Route path="/admin/market" element={<Market />} />
                        {/* Phase 2 Block 2A: admin market detail uses :short_id (same
             template route shape as user-mode /market/:short_id).

             Push 4-1d-m (2026-07-06): admin market kind-split mirrors
             user-side. AdminMarketDetail = connector-template detail.
             AdminModelMarketDetail = model-template detail. Legacy
             /admin/market/:short_id resolves via LegacyAdminMarketRedirect
             (same short_id-prefix dispatch as user-side). */}
                        <Route
                          path="/admin/market/connectors/:short_id"
                          element={<AdminMarketDetail />}
                        />
                        <Route
                          path="/admin/market/models/:short_id"
                          element={<AdminModelMarketDetail />}
                        />
                        <Route
                          path="/admin/market/:short_id"
                          element={<LegacyAdminMarketRedirect />}
                        />
                        <Route
                          path="/admin/catalog"
                          element={<Navigate to="/admin/market" replace />}
                        />
                        <Route path="/admin/people" element={<People />} />
                        {/* Phase 2 Block 2A Cluster C — :id (UUID) → :short_id (usr-prefix). */}
                        <Route path="/admin/people/:short_id" element={<PersonDetail />} />
                        <Route path="/admin/connectors" element={<AdminConnectors />} />
                        {/* Push 84b (D-199) — BYO Pattern C guided install wizard. Must
             precede the /admin/connectors/:short_id route so "byo" isn't
             captured as a short_id. */}
                        <Route
                          path="/admin/connectors/byo"
                          element={<AdminBYOConnectorInstall />}
                        />
                        {/* Phase 2 Block 2A: admin connector URL flattened from
             /admin/connectors/instance/:instance_id → /admin/connectors/:short_id.
             The /instance/ path segment was a UUID-shape artifact; short_id's
             cni prefix already encodes "this is a connector instance." */}
                        <Route
                          path="/admin/connectors/:short_id"
                          element={<AdminConnectorDetail />}
                        />
                        <Route path="/admin/permission-sets" element={<PermissionSets />} />
                        {/* D-161: Per-set workshop — the 90% admin surface. Configure
              users + connectors + prompts + models inline without leaving
              the page. */}
                        {/* Phase 2 Block 2A Cluster C — :id (UUID) → :short_id (pst-prefix). */}
                        <Route
                          path="/admin/permission-sets/:short_id"
                          element={<PermissionSetDetail />}
                        />
                        <Route path="/admin/assignments" element={<AdminAssignments />} />
                        <Route path="/admin/prompts" element={<AdminKnowledge />} />
                        {/* Phase 2 Block 2A Cluster B — :id (UUID) → :short_id.
             URL flattened from /admin/prompts/instance/:id (no nested segment
             carried over from Cluster A's pattern). */}
                        <Route path="/admin/prompts/:short_id" element={<AdminPromptDetail />} />
                        <Route
                          path="/admin/knowledge"
                          element={<Navigate to="/admin/prompts" replace />}
                        />
                        <Route path="/admin/models" element={<AdminModels />} />
                        {/* Phase 2 Block 2A Cluster B — :id (UUID) → :short_id. */}
                        <Route path="/admin/models/:short_id" element={<AdminModelDetail />} />
                        <Route path="/admin/credentials" element={<AdminCredentials />} />
                        {/* Sub-agent TTTTT (2026-07-31) — Analytics 3-tab IA per the
              "60k admin test". Prior 4-tab IA (Overview / Spend / Activity
              / Insights) collapsed to 3 (Overview / Reports / Activity)
              because Overview + Insights both embedded the same
              AdminInsights body, and "Spend" mis-labeled a surface that
              actually held 4 pivots (user / capability / tag / day).
              Retired routes redirect to their new homes below so
              bookmarks + external links stay live. */}
                        <Route path="/admin/analytics" element={<AnalyticsOverview />} />
                        <Route path="/admin/analytics/reports" element={<AnalyticsReports />} />
                        <Route path="/admin/analytics/activity" element={<AnalyticsActivity />} />

                        {/* Retired /admin/analytics/spend + /admin/analytics/insights
              routes. Spend collapsed into Reports (with the ?tab= legacy
              key mapping onto ?pivot= inside AnalyticsReports).
              Insights collapsed into Overview (callouts embedded there). */}
                        <Route
                          path="/admin/analytics/spend"
                          element={<Navigate to="/admin/analytics/reports?pivot=user" replace />}
                        />
                        <Route
                          path="/admin/analytics/spend/user"
                          element={<Navigate to="/admin/analytics/reports?pivot=user" replace />}
                        />
                        <Route
                          path="/admin/analytics/spend/capability"
                          element={
                            <Navigate to="/admin/analytics/reports?pivot=capability" replace />
                          }
                        />
                        <Route
                          path="/admin/analytics/spend/tag"
                          element={<Navigate to="/admin/analytics/reports?pivot=tag" replace />}
                        />
                        <Route
                          path="/admin/analytics/spend/day"
                          element={<Navigate to="/admin/analytics/reports?pivot=day" replace />}
                        />
                        <Route
                          path="/admin/analytics/insights"
                          element={<Navigate to="/admin/analytics" replace />}
                        />

                        {/* Legacy /admin/activity/* redirects. Raw event feed lands at
              /admin/analytics/activity; everything else disperses. */}
                        <Route
                          path="/admin/activity"
                          element={<Navigate to="/admin/analytics/activity" replace />}
                        />
                        <Route
                          path="/admin/activity/dispatches"
                          element={<Navigate to="/admin/analytics/activity" replace />}
                        />
                        <Route
                          path="/admin/activity/runs"
                          element={<Navigate to="/admin/analytics/activity" replace />}
                        />
                        <Route
                          path="/admin/activity/spend"
                          element={<Navigate to="/admin/analytics/reports?pivot=user" replace />}
                        />
                        <Route
                          path="/admin/activity/audit"
                          element={<Navigate to="/admin/analytics/activity?tab=audit" replace />}
                        />
                        <Route
                          path="/admin/activity/approvals"
                          element={<Navigate to="/admin/analytics/activity?tab=audit" replace />}
                        />
                        <Route
                          path="/admin/activity/transactions"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />

                        {/* Legacy /admin/billing/spend-by-* — SpendBy* components were
              orphaned when Billing IA collapsed; they now render as
              pivots under AnalyticsReports. */}
                        <Route
                          path="/admin/billing/spend-by-user"
                          element={<Navigate to="/admin/analytics/reports?pivot=user" replace />}
                        />
                        <Route
                          path="/admin/billing/spend-by-model"
                          element={
                            <Navigate to="/admin/analytics/reports?pivot=capability" replace />
                          }
                        />
                        <Route
                          path="/admin/billing/spend-by-connector"
                          element={
                            <Navigate to="/admin/analytics/reports?pivot=capability" replace />
                          }
                        />
                        <Route
                          path="/admin/billing/spend-by-prompt"
                          element={
                            <Navigate to="/admin/analytics/reports?pivot=capability" replace />
                          }
                        />
                        {/* Push 4-1d-s Item 2 (2026-07-06) — admin-scoped activity record
              detail. Mirrors /activity/:short_id under UserShell but hits
              /api/admin/activity/:short_id (requireAdminContext, RLS
              p_evt_select_admin, full account visibility) and its
              back-link + drill targets point at /admin/* routes so admins
              stay in the admin surface tree. Thin wrapper over the shared
              ActivityDetail component with mode='admin' fixed. */}
                        <Route path="/admin/activity/:short_id" element={<AdminActivityDetail />} />
                        {/* Push #10 — Admin Actions + Usage consolidated into Activity tabs.
              Push 4-1f (2026-07-08): /admin/usage now redirects to
              /admin/billing/transactions (Transactions moved from Activity
              to Billing). /admin/admin-actions still redirects to
              /admin/activity/audit.
              Push 5-A-cont (2026-07-11): "Usage" retired from vocabulary
              per D-196 sub-clause; legacy /admin/usage redirects to
              /admin/activity (free-event lens) since the two blocks it
              hosted split cleanly — by_user rollup → spend view at
              /admin/activity/spend, by_connector rollup → activity view
              at /admin/activity. Orphan Usage.tsx page deleted this push. */}
                        <Route
                          path="/admin/admin-actions"
                          element={<Navigate to="/admin/analytics/activity?tab=audit" replace />}
                        />
                        <Route
                          path="/admin/usage"
                          element={<Navigate to="/admin/analytics/activity" replace />}
                        />
                        {/* Push 4-1f: /admin/billing now a parent with 4 children.
             /admin/billing itself lands on Overview; each child gets its
             own path-based route wrapping AdminBillingWithLegacyTabRedirects
             so ?tab= legacy params still resolve at the top-level shell. */}
                        {/* Push 5-post-smoke-19 (2026-07-14) — /admin/insights lands on
             the new Analytics Overview (which absorbs the AdminInsights
             body under a proper Analytics hero + spend snapshot cards). */}
                        <Route
                          path="/admin/insights"
                          element={<Navigate to="/admin/analytics" replace />}
                        />
                        {/* Push 5-post-smoke-4 (2026-07-11) — Billing folded into
             Workspace. Legacy /admin/billing + all children redirect to
             the new /admin/workspace/billing home.
             Push 5-post-smoke-20 (2026-07-14) — inner-tab shape collapsed;
             Buy → Usage (that's where the buy-credits button lives now);
             everything else lands on the simplified single Billing page. */}
                        <Route
                          path="/admin/billing"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/billing/overview"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/billing/buy"
                          element={<Navigate to="/admin/workspace/usage" replace />}
                        />
                        <Route
                          path="/admin/billing/payment-methods"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/billing/transactions"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route path="/admin/billing/advanced" element={<AdminBillingAdvanced />} />
                        {/* Phase 2 Block 2B Tier 2 (2026-06-30) — admin token-grant
              record detail. :short_id is billing__token_grant.short_id
              (gnt-prefix). Hero "Download receipt" button when source =
              stripe_purchase + stripe_payment_intent_id is set; debits
              tab shows linked consumption rows. Admin-only via shell. */}
                        <Route path="/admin/grants/:short_id" element={<TokenGrantDetail />} />
                        {/* Phase 2 Block 2B Tier 2 (2026-06-30) — admin debit record
              detail. :short_id is billing__token_debit.short_id (dbt-
              prefix). Read-only audit row; reversal happens via
              source_kind='refund_reversal' inserts, not via mutation. */}
                        <Route path="/admin/debits/:short_id" element={<TokenDebitDetail />} />
                        {/* Legacy approval-flow routes redirect to People > Requests
              (approval workflow retired per D-198 Migration 0158). */}
                        <Route
                          path="/admin/approvals"
                          element={<Navigate to="/admin/people?tab=requests" replace />}
                        />
                        <Route
                          path="/admin/approvals/:short_id"
                          element={<Navigate to="/admin/people?tab=requests" replace />}
                        />
                        <Route
                          path="/admin/requests/:short_id"
                          element={<Navigate to="/admin/people?tab=requests" replace />}
                        />
                        {/* Phase 2 Block 2B Tier 2 (2026-06-30) — admin Notification
              record wrap. Same NotificationDetail component as the user
              route at /notifications/:short_id; mounted under AdminShell
              chrome (TopNav + AdminSubNav). RLS Tier 3 already allows
              admin/owner to read any account_user's notification row;
              the wrap is purely route + chrome. */}
                        {/* Push 5-post-smoke-13 Sub-agent D (2026-07-14) — full detail
              page for the workspace notification recipient group. Prior
              shape was modal-only (RecipientGroupModal opens from
              /admin/workspace/notifications' "Manage recipients"
              button); the modal stays for quick management, the page
              is the "open full view" destination with tab shape
              (Details / Members). One group per workspace today, so
              no :short_id in the URL. Mounted BEFORE the dynamic
              `/admin/notifications/:short_id` route so react-router
              picks the static segment on match — belt on top of
              react-router's specificity suspenders. */}
                        <Route
                          path="/admin/notifications/recipient-group"
                          element={<NotificationRecipientGroupDetail />}
                        />
                        <Route
                          path="/admin/notifications/:short_id"
                          element={<NotificationDetail />}
                        />
                        {/* Phase 2 Block 2B Tier 2 (2026-06-30) — admin invitation
              record detail. :short_id is sys__user_invitation.short_id
              (inv-prefix). Block 2C spice: Resend / Delete buttons in
              hero + Copy invite link affordance in body card (Pending
              state only). Reuses existing /resend + /delete UUID-shaped
              endpoints via the GET response's `id` field. */}
                        <Route path="/admin/invitations/:short_id" element={<InvitationDetail />} />
                        {/* Phase 2 Block 2B Tier 4 (2026-06-30) — junction audit pages.
              9 routes sharing JunctionRecordStub primitive + dispatcher
              endpoint /api/admin/junction/:kind/:short_id. Category H per
              OC framework: junction-symmetric per D-161 (mutations live
              on parent-record detail pages; this stub is audit surface).
              Must sit before /admin/* catch-all to win specificity.
              Push 4-1d-k-really-final (2026-07-02): harmonized 7 legacy
              routes onto `/admin/junction/{kind}/:short_id` so Category H
              pages are discoverable by URL prefix alone. Legacy paths
              (permission-set-user/-connector/-model/-prompt,
              connector/model/prompt-assignment) redirect via
              LegacyJunctionRedirect stubs — kept below the new routes
              for grep clarity. */}
                        <Route
                          path="/admin/junction/permission-set-user/:short_id"
                          element={<PermissionSetUserJct />}
                        />
                        <Route
                          path="/admin/junction/permission-set-connector/:short_id"
                          element={<PermissionSetConnectorJct />}
                        />
                        <Route
                          path="/admin/junction/permission-set-prompt/:short_id"
                          element={<PermissionSetPromptJct />}
                        />
                        <Route
                          path="/admin/junction/permission-set-model/:short_id"
                          element={<PermissionSetModelJct />}
                        />
                        <Route
                          path="/admin/junction/connector-assignment/:short_id"
                          element={<ConnectorAssignmentJct />}
                        />
                        <Route
                          path="/admin/junction/user-connector-authorization/:short_id"
                          element={<UserConnectorAuthJct />}
                        />
                        <Route
                          path="/admin/junction/model-assignment/:short_id"
                          element={<ModelAssignmentJct />}
                        />
                        <Route
                          path="/admin/junction/prompt-assignment/:short_id"
                          element={<PromptAssignmentJct />}
                        />
                        <Route
                          path="/admin/junction/chat-session-assignment/:short_id"
                          element={<ChatSessionAssignmentJct />}
                        />
                        {/* Legacy junction redirects — preserve pre-rename bookmarks +
              external notification links that referenced the pre-harmonized
              paths. Push 4-1d-k-really-final (2026-07-02). */}
                        <Route
                          path="/admin/permission-set-user/:short_id"
                          element={<LegacyPermissionSetUserRedirect />}
                        />
                        <Route
                          path="/admin/permission-set-connector/:short_id"
                          element={<LegacyPermissionSetConnectorRedirect />}
                        />
                        <Route
                          path="/admin/permission-set-model/:short_id"
                          element={<LegacyPermissionSetModelRedirect />}
                        />
                        <Route
                          path="/admin/permission-set-prompt/:short_id"
                          element={<LegacyPermissionSetPromptRedirect />}
                        />
                        <Route
                          path="/admin/connector-assignment/:short_id"
                          element={<LegacyConnectorAssignmentRedirect />}
                        />
                        <Route
                          path="/admin/model-assignment/:short_id"
                          element={<LegacyModelAssignmentRedirect />}
                        />
                        <Route
                          path="/admin/prompt-assignment/:short_id"
                          element={<LegacyPromptAssignmentRedirect />}
                        />
                        {/* Push 4-1d-k-truly-final (2026-07-02) — remaining 2 junction routes. */}
                        <Route
                          path="/admin/user-connector-authorization/:short_id"
                          element={<LegacyUserConnectorAuthorizationRedirect />}
                        />
                        <Route
                          path="/admin/chat-session-assignment/:short_id"
                          element={<LegacyChatSessionAssignmentRedirect />}
                        />
                        {/* Phase 2 Block 2B Tier 3 / 6 / 7 / 48th (2026-06-30) — remaining
              10 sub-record + template + product + notification_recipient
              admin routes. All share SubRecordDetail primitive +
              dispatcher endpoint at /api/admin/sub-record/:kind/:short_id.
              Completes Universal-or-Nothing coverage across all 48 tables. */}
                        <Route
                          path="/admin/resources/:short_id"
                          element={<ResourceInstanceAdmin />}
                        />
                        <Route
                          path="/admin/mcp-prompts/:short_id"
                          element={<McpPromptInstanceAdmin />}
                        />
                        <Route
                          path="/admin/tool-templates/:short_id"
                          element={<ToolTemplateAdmin />}
                        />
                        <Route
                          path="/admin/resource-templates/:short_id"
                          element={<ResourceTemplateAdmin />}
                        />
                        <Route
                          path="/admin/mcp-prompt-templates/:short_id"
                          element={<McpPromptTemplateAdmin />}
                        />
                        <Route
                          path="/admin/provider-templates/:short_id"
                          element={<ProviderTemplateAdmin />}
                        />
                        <Route path="/admin/products/:short_id" element={<ProductAdmin />} />
                        <Route
                          path="/admin/notification-recipients/:short_id"
                          element={<NotificationRecipientAdmin />}
                        />
                        {/* Phase 2 closure GAP #4a/b (2026-06-30) — Key + Provider admin pages */}
                        <Route path="/admin/keys/:short_id" element={<KeyDetail />} />
                        <Route path="/admin/providers/:short_id" element={<ProviderDetail />} />
                        {/* GAP #4c admin variant */}
                        {/* Push 4-1c-c (2026-07-01): /admin/market/knowledge route removed. */}
                        {/* Phase 4 pre-plumbing (2026-06-30) — documents capability admin */}
                        {/* Push 4-1b (2026-07-01): /admin/documents → /admin/library.
             Same substitution as user side. Folder admin routes below. */}
                        <Route path="/admin/library" element={<AdminLibraryList />} />
                        <Route path="/admin/library/:short_id" element={<DocumentDetailAdmin />} />
                        <Route
                          path="/admin/library/folders/:short_id"
                          element={<FolderDetailAdmin />}
                        />
                        <Route
                          path="/admin/junction/folder-assignment/:short_id"
                          element={<FolderAssignmentJct />}
                        />
                        <Route
                          path="/admin/junction/permission-set-folder/:short_id"
                          element={<PermissionSetFolderJct />}
                        />
                        {/* /admin/incidents route DROPPED Push #6 (2026-06-25) per Ean lock
              2026-06-24 + D-186 Pattern 5a: incident management is operator-side
              only (control lane Phase 2+). Customer-facing incident state =
              sys__notification with kind='incident_broadcast' (banner
              notification), NOT an admin manage page. */}
                        {/* Push 5-post-smoke-20 (2026-07-14): /admin/workspace is now a
             4-tab hub (General / Usage / Billing / Notifications). Workspace
             promoted to a sidenav parent (was leaf); Usage is the new
             money-consumption page — team pot balance, monthly cap, auto-
             reload, and the Advanced expander for per-user allocations.
             Billing trimmed to payment method + auto-reload status +
             invoices. Path-based routing preferred; ?tab= remains for
             backwards-compat. Old billing inner routes (/buy /payment-methods
             /transactions) collapse to /admin/workspace/billing. */}
                        <Route path="/admin/workspace" element={<Settings />} />
                        {/* /general is a canonical alias for the root (General tab); redirect
             so the sidenav's General NavLink (end-matched on /admin/workspace)
             stays highlighted on bookmarked deep-links. */}
                        <Route
                          path="/admin/workspace/general"
                          element={<Navigate to="/admin/workspace" replace />}
                        />
                        <Route path="/admin/workspace/usage" element={<Settings />} />
                        <Route path="/admin/workspace/billing" element={<Settings />} />
                        <Route path="/admin/workspace/notifications" element={<Settings />} />
                        {/* Legacy /admin/workspace/overview → General (pre-Push-5-post-smoke-20). */}
                        <Route
                          path="/admin/workspace/overview"
                          element={<Navigate to="/admin/workspace" replace />}
                        />
                        {/* Legacy /admin/workspace/billing/* inner routes → /admin/workspace/billing (Push
             5-post-smoke-20 collapsed the 4-inner-tab billing into a single simple page). */}
                        <Route
                          path="/admin/workspace/billing/overview"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/workspace/billing/buy"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/workspace/billing/payment-methods"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/workspace/billing/transactions"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        {/* Legacy /admin/setup/* — pre-Push-4-1f Setup parent. */}
                        <Route
                          path="/admin/setup"
                          element={<Navigate to="/admin/workspace" replace />}
                        />
                        <Route
                          path="/admin/setup/workspace"
                          element={<Navigate to="/admin/workspace" replace />}
                        />
                        <Route
                          path="/admin/setup/notifications"
                          element={<Navigate to="/admin/workspace/notifications" replace />}
                        />
                        <Route
                          path="/admin/setup/billing"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/setup/billing/overview"
                          element={<Navigate to="/admin/workspace/billing" replace />}
                        />
                        <Route
                          path="/admin/setup/billing/buy"
                          element={<Navigate to="/admin/workspace/usage" replace />}
                        />
                        <Route
                          path="/admin/settings"
                          element={<Navigate to="/admin/workspace" replace />}
                        />
                        {/* Push 4-1e-cleanup-fu2 (2026-07-08): /admin/inbox retired — its
             content (help/support events + approvals) folds into People >
             Requests tab.
             Sub-agent WWWWW (2026-07-31): canonical query key aligned to
             ?tab=requests (matches on-page tab label + AdminSubNav search
             hit). People.tsx normalizePeople still accepts legacy
             ?tab=approvals for external bookmarks. */}
                        <Route
                          path="/admin/inbox"
                          element={<Navigate to="/admin/people?tab=requests" replace />}
                        />
                        {/* Push 4-1e-cleanup-fu2 (2026-07-08): /admin/requests folds into
             People > Requests tab. */}
                        <Route
                          path="/admin/requests"
                          element={<Navigate to="/admin/people?tab=requests" replace />}
                        />
                        {/* Phase 2 Block 2B Tier 5 (2026-06-30) — operator-only record
              stubs. Each accepts an optional :short_id (admin can land on
              the bare table-kind URL OR a specific row URL; both render
              the same "platform owns this" shell). Order: more specific
              :short_id route MUST come before the catch-all below. The
              bare-landing variants are needed because notification banners
              + audit chips link to the table-level URL when no specific
              row applies. */}
                        <Route path="/admin/pricing-config" element={<PricingConfigStub />} />
                        <Route
                          path="/admin/pricing-config/:short_id"
                          element={<PricingConfigStub />}
                        />
                        <Route path="/admin/account-routing" element={<AccountRoutingStub />} />
                        <Route
                          path="/admin/account-routing/:short_id"
                          element={<AccountRoutingStub />}
                        />
                        <Route path="/admin/user-routing" element={<UserRoutingStub />} />
                        <Route path="/admin/user-routing/:short_id" element={<UserRoutingStub />} />
                        <Route path="/admin/oauth-client" element={<OAuthClientStub />} />
                        <Route path="/admin/oauth-client/:short_id" element={<OAuthClientStub />} />
                        <Route
                          path="/admin/oauth-authorization"
                          element={<OAuthAuthorizationStub />}
                        />
                        <Route
                          path="/admin/oauth-authorization/:short_id"
                          element={<OAuthAuthorizationStub />}
                        />
                        <Route path="/admin/operator-alert" element={<OperatorAlertStub />} />
                        <Route
                          path="/admin/operator-alert/:short_id"
                          element={<OperatorAlertStub />}
                        />
                        <Route path="/admin/incident" element={<IncidentStub />} />
                        <Route path="/admin/incident/:short_id" element={<IncidentStub />} />
                        <Route path="/admin/stripe-webhook" element={<StripeWebhookStub />} />
                        <Route
                          path="/admin/stripe-webhook/:short_id"
                          element={<StripeWebhookStub />}
                        />
                        <Route path="/admin/schema-migration" element={<SchemaMigrationStub />} />
                        <Route
                          path="/admin/schema-migration/:short_id"
                          element={<SchemaMigrationStub />}
                        />
                        {/* Push 4-0 (2026-07-01) — Category G stub route for llm__key_shared */}
                        <Route path="/admin/keys-shared" element={<LlmKeySharedStub />} />
                        <Route path="/admin/keys-shared/:short_id" element={<LlmKeySharedStub />} />
                        {/* Round 10 Item 76 — admin catch-all. Without this, unknown
              /admin/* URLs (e.g. /admin/connectors/<slug-not-uuid>, a typo'd
              path, or a stale external link) render an empty AdminShell with
              no content. Surface the branded NotFound so the chrome shows
              an honest "we couldn't find that" instead of a blank pane.
              Canonical detail-page URL shapes are by-UUID:
                /admin/connectors/instance/<instance_id>
                /admin/models/<id>
                /admin/prompts/<id>
              Slug-based deep links are NOT supported; list pages link to the
              canonical UUID path. */}
                        <Route path="/admin/*" element={<NotFound />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </BuyCreditsProvider>
              </RouteProgressProvider>
            </ThemeProvider>
          </BrandThemeProvider>
        </SessionProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
